<template>
  <div v-if="value" class="nr-backdrop" @click.self="close">
    <div class="nr-dialog" role="dialog" aria-modal="true" aria-labelledby="nr-title">
      <div class="nr-head">
        <div>
          <h5 id="nr-title" class="mb-0">Narrative report</h5>
          <small class="text-muted">A written overview of the agency's data with charts, comparisons and points to note, as Word or PDF.</small>
        </div>
        <button class="btn btn-sm btn-link text-secondary" @click="close" aria-label="Close"><i class="fe fe-x"></i></button>
      </div>

      <div class="nr-body">
        <label class="nr-label">Chapters</label>
        <div class="nr-sections">
          <label v-for="s in sections" :key="s.key" class="nr-check" :class="{ on: chosen.includes(s.key) }">
            <input type="checkbox" :value="s.key" v-model="chosen" />
            <span><strong>{{ s.title }}</strong><small>{{ s.hint }}</small></span>
          </label>
        </div>
        <div class="d-flex mt-2" style="gap: 12px;">
          <a href="#" class="small" @click.prevent="chosen = sections.map((s) => s.key)">Select all</a>
          <a href="#" class="small" @click.prevent="chosen = []">Clear</a>
        </div>

        <div class="form-row mt-3">
          <div class="form-group col-md-4">
            <label class="nr-label">Period</label>
            <select class="form-control form-control-sm" v-model="preset" @change="applyPreset">
              <option value="last_month">Last month</option>
              <option value="last_quarter">Last quarter</option>
              <option value="last_6_months">Last 6 months</option>
              <option value="this_year">This year so far</option>
              <option value="last_year">Last year</option>
              <option value="custom">Custom dates</option>
            </select>
          </div>
          <div class="form-group col-md-4">
            <label class="nr-label">From</label>
            <input type="date" class="form-control form-control-sm" v-model="from" @change="preset = 'custom'" />
          </div>
          <div class="form-group col-md-4">
            <label class="nr-label">To</label>
            <input type="date" class="form-control form-control-sm" v-model="to" @change="preset = 'custom'" />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-4">
            <label class="nr-label">Format</label>
            <div class="btn-group btn-group-sm d-flex">
              <button class="btn" :class="format === 'docx' ? 'btn-primary' : 'btn-outline-primary'" @click="format = 'docx'"><i class="fe fe-file-text mr-1"></i> Word</button>
              <button class="btn" :class="format === 'pdf' ? 'btn-primary' : 'btn-outline-primary'" @click="format = 'pdf'"><i class="fe fe-file mr-1"></i> PDF</button>
            </div>
          </div>
          <div class="form-group col-md-8">
            <label class="nr-label">Title <span class="text-muted font-weight-normal">(optional)</span></label>
            <input class="form-control form-control-sm" v-model="title" maxlength="120" :placeholder="defaultTitle" />
          </div>
        </div>

        <div class="form-group mb-0">
          <label class="nr-check-inline">
            <input type="checkbox" v-model="byEmail" />
            <span>Prepare it in the background and send me the link by email and chat</span>
          </label>
          <small class="d-block text-muted mt-1">Recommended for long periods or all chapters, which can take a minute or more.</small>
        </div>
        <div v-if="error" class="alert alert-danger py-2 mt-3 mb-0 small">{{ error }}</div>
      </div>

      <div class="nr-foot">
        <small class="text-muted mr-auto">Compared with the previous period of the same length.</small>
        <button class="btn btn-sm btn-outline-secondary" @click="close" :disabled="busy">Cancel</button>
        <button class="btn btn-sm btn-primary" @click="generate" :disabled="busy || !chosen.length || !from || !to">
          <span v-if="busy"><span class="spinner-border spinner-border-sm mr-1"></span> Writing the report…</span>
          <span v-else><i class="fe fe-download mr-1"></i> {{ byEmail ? "Prepare and send" : "Generate" }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

const SECTIONS = [
  { key: "enrollment", title: "Enrolment", hint: "Membership, new enrolments, programmes, gender, age, LGAs" },
  { key: "encounters", title: "Encounters", hint: "Utilisation per enrollee, facilities, diagnoses" },
  { key: "authorization_codes", title: "Authorization codes", hint: "Referrals, decisions, turnaround, flows" },
  { key: "claims", title: "Claims", hint: "Amounts, stages, spend per enrollee, costly items" },
  { key: "quality_assurance", title: "Quality assurance", hint: "Coverage, scores, reported figures" },
  { key: "providers", title: "Providers", hint: "Network, access, facility performance, ratings" },
];

export default {
  name: "NarrativeReportModal",
  props: {
    value: { type: Boolean, default: false },
    initialSections: { type: Array, default: () => ["enrollment", "encounters", "authorization_codes", "claims", "quality_assurance", "providers"] },
  },
  data() {
    return { sections: SECTIONS, chosen: [...this.initialSections], preset: "last_quarter", from: "", to: "", format: "docx", title: "", byEmail: false, busy: false, error: "" };
  },
  computed: {
    defaultTitle() {
      if (this.chosen.length === 1) return SECTIONS.find((s) => s.key === this.chosen[0]).title + " report";
      return "Programme performance report";
    },
  },
  created() {
    this.applyPreset();
  },
  methods: {
    close() {
      if (!this.busy) this.$emit("input", false);
    },
    applyPreset() {
      const m = moment();
      const set = (a, b) => { this.from = a.format("YYYY-MM-DD"); this.to = b.format("YYYY-MM-DD"); };
      switch (this.preset) {
        case "last_month": set(m.clone().subtract(1, "month").startOf("month"), m.clone().subtract(1, "month").endOf("month")); break;
        case "last_quarter": set(m.clone().subtract(1, "quarter").startOf("quarter"), m.clone().subtract(1, "quarter").endOf("quarter")); break;
        case "last_6_months": set(m.clone().subtract(6, "months").startOf("month"), m.clone().subtract(1, "month").endOf("month")); break;
        case "this_year": set(m.clone().startOf("year"), m.clone()); break;
        case "last_year": set(m.clone().subtract(1, "year").startOf("year"), m.clone().subtract(1, "year").endOf("year")); break;
      }
    },
    async generate() {
      this.error = "";
      if (moment(this.to).isBefore(moment(this.from))) { this.error = "The end date is before the start date."; return; }
      const order = SECTIONS.map((s) => s.key);
      const body = { sections: order.filter((k) => this.chosen.includes(k)), from: this.from, to: this.to, format: this.format, title: this.title || undefined, deliver: this.byEmail ? "email" : "download" };
      this.busy = true;
      try {
        if (this.byEmail) {
          const res = await this.axios.post("/api/v1/auth/reports/narrative", body);
          this.$toasted.success(res.data.message, { position: "top-center", duration: 5000 });
          this.$emit("input", false);
          return;
        }
        const res = await this.axios.post("/api/v1/auth/reports/narrative", body, { responseType: "blob", timeout: 0 });
        const disposition = res.headers["content-disposition"] || "";
        const match = /filename="?([^";]+)"?/.exec(disposition);
        const name = match ? match[1] : `report_${this.from}_${this.to}.${this.format}`;
        const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] }));
        const a = document.createElement("a");
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 2000);
        this.$toasted.success("Report downloaded.", { position: "top-center", duration: 3000 });
        this.$emit("input", false);
      } catch (e) {
        this.error = await this.message(e);
      } finally {
        this.busy = false;
      }
    },
    async message(e) {
      const data = e.response && e.response.data;
      try {
        const json = data instanceof Blob ? JSON.parse(await data.text()) : data;
        if (json && json.errors) return Object.values(json.errors).flat()[0];
        if (json && json.message) return e.response.status === 429 ? "Too many reports at once. Wait a minute and try again." : json.message;
      } catch (_) { /* not JSON */ }
      return "The report could not be generated. Try a shorter period or the email option.";
    },
  },
};
</script>

<style scoped>
.nr-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45); z-index: 1060; display: flex; align-items: flex-start; justify-content: center; padding: 48px 16px; overflow-y: auto; }
.nr-dialog { background: #fff; border-radius: 12px; width: 100%; max-width: 720px; box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25); }
.nr-head { display: flex; justify-content: space-between; align-items: flex-start; padding: 18px 20px 12px; border-bottom: 1px solid #e2e8f0; }
.nr-body { padding: 16px 20px; }
.nr-foot { display: flex; align-items: center; gap: 8px; padding: 12px 20px; border-top: 1px solid #e2e8f0; }
.nr-label { font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 6px; display: block; }
.nr-sections { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 8px; }
.nr-check { display: flex; gap: 8px; align-items: flex-start; border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 10px; cursor: pointer; margin: 0; }
.nr-check.on { border-color: #1f6feb; background: #eff6ff; }
.nr-check input { margin-top: 3px; }
.nr-check span { display: flex; flex-direction: column; font-size: 13px; }
.nr-check small { color: #64748b; font-size: 11px; line-height: 1.3; }
.nr-check-inline { display: flex; gap: 8px; align-items: center; font-size: 13px; margin: 0; cursor: pointer; }
@media (max-width: 576px) { .nr-backdrop { padding: 16px; } }
</style>
